import React, { useEffect, useRef, useState } from 'react';
import useInViewAutoplay from '../../hooks/useInViewAutoplay';
import './ProjectVideo.css';

// Video player for the two kinds of clip the site carries. Self-contained per
// the encapsulation rule: the component owns its play state and controls - a
// parent just hands it the resolved asset URLs and says which kind it is.
//
// mode="demo" (default) - a short silent loop, e.g. an awning extending:
//  - muted + playsInline + loop so browsers allow autoplay and iOS doesn't take
//    over the screen.
//  - autoplays only while it is on screen (IntersectionObserver); pauses when it
//    scrolls out, so it isn't burning battery off-screen.
//  - respects prefers-reduced-motion: no autoplay, poster stays until the
//    visitor presses play.
//  - a slim play/pause + scrub bar of our own.
//
// mode="guide" - a longer piece the visitor sits down to watch, e.g. the
// installation guide:
//  - sound on, no loop, never autoplays; the poster and a large play button
//    until the visitor chooses to start.
//  - once started, the browser's native controls take over (volume,
//    fullscreen, scrubbing, captions) - they are the reliable choice for a
//    three-minute video on a phone.
//
// Both: webm + mp4 sources, poster frame first, orientation read from the file.
export default function ProjectVideo({ video, title, mode = 'demo' }) {
  const guide = mode === 'guide';
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  // Guide mode: has the visitor pressed play yet? Drives the poster overlay
  // and when native controls appear.
  const [started, setStarted] = useState(false);
  // Orientation is read from the file itself once metadata loads, so a portrait
  // phone clip is shown tall (letterboxed to a sensible height) and a landscape
  // clip fills the usual 16:9 frame. No admin toggle to remember, and it works
  // for whatever gets uploaded next.
  const [portrait, setPortrait] = useState(false);
  // Scrub bar state. `duration` is 0 until metadata loads; `seeking` freezes
  // the displayed position while the visitor drags so it doesn't fight
  // timeupdate events.
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  // Mirrored in a ref because the mount-time effect's listeners would
  // otherwise close over the initial state value forever.
  const seekingRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Keep React state in step even when the browser plays/pauses on its own.
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onMeta = () => {
      if (el.videoHeight > el.videoWidth) setPortrait(true);
      if (Number.isFinite(el.duration)) setDuration(el.duration);
    };
    const onTime = () => { if (!seekingRef.current) setCurrent(el.currentTime); };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('timeupdate', onTime);
    // Metadata may already be available (cached / fast load).
    if (el.readyState >= 1) onMeta();

    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('timeupdate', onTime);
    };
  }, []);

  // Demo clips play while on screen; a guide waits for the visitor.
  useInViewAutoplay(ref, !guide);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  const start = () => {
    const el = ref.current;
    if (!el) return;
    setStarted(true);
    el.play().catch(() => {});
  };

  // Scrub. While dragging we update the displayed position immediately and
  // seek the media, but suppress timeupdate so the thumb doesn't jump back.
  const onScrub = (e) => {
    const el = ref.current;
    const t = Number(e.target.value);
    setCurrent(t);
    if (el && Number.isFinite(t)) el.currentTime = t;
  };
  const startScrub = () => { seekingRef.current = true; };
  const endScrub = () => { seekingRef.current = false; };

  const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const classes = [
    'asg-featured-video',
    portrait ? 'asg-featured-video--portrait' : '',
    guide ? 'asg-featured-video--guide' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <video
        ref={ref}
        className="asg-featured-video-el"
        muted={!guide}
        loop={!guide}
        controls={guide && started}
        playsInline
        preload="metadata"
        poster={video.poster}
        width={1280}
        height={720}
        aria-label={video.alt || `${title} - video`}
      >
        {video.webm && <source src={video.webm} type="video/webm" />}
        {video.mp4 && <source src={video.mp4} type="video/mp4" />}
      </video>
      {guide && !started && (
        <button
          type="button"
          className="asg-featured-video-start"
          onClick={start}
          aria-label={`Play ${title}`}
        >
          <span className="asg-featured-video-start-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v13.72a1 1 0 0 0 1.52.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" /></svg>
          </span>
          {video.duration && <span className="asg-featured-video-start-label">{video.duration}</span>}
        </button>
      )}
      {/* Demo mode control bar: play/pause plus a scrub bar so the visitor can
          jump to any point rather than only starting and stopping. Guide mode
          hands this job to the browser's native controls. */}
      {!guide && (
      <div className="asg-featured-video-bar">
        <button
          type="button"
          className="asg-featured-video-btn"
          onClick={toggle}
          aria-pressed={playing}
          aria-label={playing ? 'Pause video' : 'Play video'}
        >
          {playing ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5.14v13.72a1 1 0 0 0 1.52.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
            </svg>
          )}
        </button>

        <input
          type="range"
          className="asg-featured-video-scrub"
          min={0}
          max={duration || 0}
          step="any"
          value={Math.min(current, duration || 0)}
          onChange={onScrub}
          onPointerDown={startScrub}
          onPointerUp={endScrub}
          onKeyDown={startScrub}
          onKeyUp={endScrub}
          disabled={!duration}
          aria-label="Seek video"
          aria-valuetext={`${fmt(current)} of ${fmt(duration)}`}
          style={{ '--asg-video-progress': `${duration ? (current / duration) * 100 : 0}%` }}
        />

        <span className="asg-featured-video-time">{fmt(current)} / {fmt(duration)}</span>
      </div>
      )}
    </div>
  );
}
